/* eslint-disable max-lines */
import React from 'react';
import _ from 'lodash';
import keys from './../kit/keymap';
import Row from './row';
import { CommonProps, Column } from '../kit/interface';
import { SpreadsheetGridStyle, SpreadsheetGridPlaceholder } from '../../styles';
import { emulateTab } from 'emulate-tab';

interface Props extends CommonProps {
  allRows: any[];
  offset: number;
  startIndex: number;
  rowsCount: number;
  disabledCells: { x: number; y: number }[];
  onCellMove?: ({ x, y }: { x: number; y: number }) => void;
  focusedCell?: { x: number; y: number } | null;
  columnWidthValues: { [key in string | number]: number };
}

interface State {
  allRows: any[];
  activeCell?: { x: number; y: number } | null;
  focusedCell?: { x: number; y: number } | null;
}

class SpreadsheetGrid extends React.PureComponent<Props, State> {
  skipGlobalClick: boolean;
  constructor(props: Props) {
    super(props);

    this.onGlobalKeyDown = this.onGlobalKeyDown.bind(this);
    this.onGlobalClick = this.onGlobalClick.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
    this.onCellDoubleClick = this.onCellDoubleClick.bind(this);
    this.getCellClassName = this.getCellClassName.bind(this);

    this.skipGlobalClick = false;

    this.state = {
      allRows: this.props.allRows,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onGlobalKeyDown, false);
    document.addEventListener('click', this.onGlobalClick, false);
  }

  static getDerivedStateFromProps(
    nextProps: { disabledCells: { x: number; y: number }[]; allRows: any[] },
    prevState: {
      activeCell: _.ListIteratorTypeGuard<unknown, unknown>;
      focusedCell: _.ListIteratorTypeGuard<unknown, unknown>;
      allRows: any[];
    },
  ) {
    const disabledCells = nextProps.disabledCells;
    const nextState: {
      allRows: any[];
      activeCell?: { x: number; y: number } | null;
      focusedCell?: { x: number; y: number } | null;
    } = {
      allRows: nextProps.allRows,
    };

    if (_.find(disabledCells, prevState.activeCell)) {
      nextState.activeCell = null;
    }
    if (_.find(disabledCells, prevState.focusedCell)) {
      nextState.focusedCell = null;
    }

    if (prevState.allRows !== nextProps.allRows) {
      nextState.focusedCell = null;
    }

    return nextState;
  }

  focusCell(nextFocusedCell: { x: number; y: number } | null) {
    this.setState({
      activeCell: nextFocusedCell,
      focusedCell: nextFocusedCell,
    });
    this.skipGlobalClick = true;
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    document.removeEventListener('keydown', this.onGlobalKeyDown, false);
    document.addEventListener('keydown', this.onGlobalKeyDown, false);
    this.onActiveCellChanged(prevState.activeCell);
    if (this.props.focusedCell) {
      this.skipGlobalClick = true;
    }
  }

  onActiveCellChanged(prevCell: { x: number; y: number } | null | undefined) {
    const { onActiveCellChanged } = this.props;
    const newCell = this.state.activeCell;

    if (onActiveCellChanged && newCell !== prevCell) {
      onActiveCellChanged(newCell ? newCell : null);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onGlobalKeyDown, false);
    document.removeEventListener('click', this.onGlobalClick, false);
  }

  onGlobalKeyDown(e: {
    keyCode: number;
    preventDefault: () => void;
    stopPropagation: () => void;
    shiftKey: any;
    key: string;
  }) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const block = this;
    const columnsCount = this.props.columns.length;
    const rowsCount = this.props.rowsCount;

    let newActiveCell = this.state.activeCell;
    let newFocusedCell = this.state.focusedCell;
    const onCellMove = this.props.onCellMove;

    function moveRight({ x, y }: { x: number; y: number }) {
      if (y < columnsCount - 1) {
        newActiveCell = { x, y: y + 1 };
      } else if (x < rowsCount - 1) {
        newActiveCell = { x: x + 1, y: 0 };
      }
      newFocusedCell = null;

      if (_.find(block.props.disabledCells, newActiveCell ? newActiveCell : undefined)) {
        newActiveCell && moveRight(newActiveCell);
      }
    }

    function moveDown({ x, y }: { x: number; y: number }) {
      if (x < rowsCount - 1) {
        newActiveCell = { x: x + 1, y };
      }
      newFocusedCell = null;

      if (_.find(block.props.disabledCells, newActiveCell ? newActiveCell : undefined)) {
        newActiveCell && moveDown(newActiveCell);
      }
    }

    function moveUp({ x, y }: { x: number; y: number }) {
      if (x > 0) {
        newActiveCell = { x: x - 1, y };
      }
      newFocusedCell = null;

      if (_.find(block.props.disabledCells, newActiveCell ? newActiveCell : undefined)) {
        newActiveCell && moveUp(newActiveCell);
      }
    }

    function moveLeft({ x, y }: { x: number; y: number }) {
      if (y > 0) {
        newActiveCell = { x, y: y - 1 };
      } else if (x > 0) {
        newActiveCell = { x: x - 1, y: columnsCount - 1 };
      }
      newFocusedCell = null;

      if (_.find(block.props.disabledCells, newActiveCell ? newActiveCell : undefined)) {
        newActiveCell && moveLeft(newActiveCell);
      }
    }

    if (this.state.activeCell) {
      const { x, y } = this.state.activeCell;

      newFocusedCell = this.state.activeCell;

      // SHIFT + ARROW DOWN
      if (e.shiftKey && e.keyCode === 40) {
        moveDown({ x, y });
      }

      // SHIFT + ARROW UP
      if (e.shiftKey && e.keyCode === 38) {
        moveUp({ x, y });
      }

      // TAB or SHIFT + ARROW LEFT
      if ((e.shiftKey && e.key === 'Tab') || (e.shiftKey && e.keyCode === 37)) {
        if (this.state.focusedCell) {
          if (this.state.focusedCell.x === 0 && this.state.focusedCell.y === 0) return
          moveLeft({ x, y });
          if (onCellMove) {
            onCellMove({ x: y - 1, y: x });
          }
        } else {
          newFocusedCell = this.state.activeCell;
        }

        e.preventDefault();
        e.stopPropagation();
        this.setState({
          activeCell: newActiveCell,
          focusedCell: newFocusedCell,
        });
        return;
      }

      // TAB or SHIFT + ARROW RIGHT
      if ((e.keyCode === keys.TAB) || (e.shiftKey && e.keyCode === 39)) {
        emulateTab();
        moveRight({ x, y });
        if (onCellMove) {
          onCellMove({ x: y + 1, y: x });
        }

        e.preventDefault();
        e.stopPropagation();
      }

      if (e.keyCode === keys.ESC) {
        if (this.state.focusedCell) {
          newFocusedCell = null;
        } else if (this.state.activeCell) {
          newActiveCell = null;
          newFocusedCell = null;
        }
      }

      this.setState({
        activeCell: newActiveCell,
        focusedCell: newFocusedCell,
      });
    }
  }

  onGlobalClick() {
    if (!this.skipGlobalClick) {
      this.setState({
        activeCell: null,
        focusedCell: null,
      });
    } else {
      this.skipGlobalClick = false;
    }
  }

  onCellClick(x: number, y: number, row: any, columnId: number, e: any) {
    if (!_.find(this.props.disabledCells, { x, y })) {
      if (!e.skipCellClick && !_.isEqual(this.state.focusedCell, { x, y })) {
        this.setState({
          focusedCell: this.props.focusOnSingleClick ? { x, y } : e.target !== e.currentTarget ? { x, y } : null,
          activeCell: { x, y },
        });
      }
    }

    if (this.props.onCellClick) {
      this.props.onCellClick(row, columnId);
    }

    this.skipGlobalClick = true;
  }

  onCellDoubleClick(x: number, y: number) {
    if (!_.find(this.props.disabledCells, { x, y })) {
      this.setState({
        activeCell: { x, y },
        focusedCell: { x, y },
      });
    }
  }

  getCellClassName(column: Column, row: any, x: number, y: number) {
    return (
      (_.isEqual(this.state.activeCell, { x, y }) ? ' SpreadsheetGrid__cell_active' : '') +
      (_.isEqual(this.state.focusedCell, { x, y }) ? ' SpreadsheetGrid__cell_focused' : '') +
      (_.find(this.props.disabledCells, { x, y }) ? ' SpreadsheetGrid__cell_disabled' : '') +
      (column.getCellClassName ? ' ' + column.getCellClassName(row) : '')
    );
  }

  calculatePosition() {
    return this.props.offset + 'px';
  }

  renderBody() {
    const { rows, columns, startIndex } = this.props;

    let body;

    if (rows.length) {
      body = rows.map((row, i) => {
        return (
          <Row
            x={startIndex + i}
            maxX={rows.length}
            key={this.props.getRowKey(row)}
            columns={columns}
            row={row}
            getCellClassName={this.getCellClassName}
            onCellClick={this.onCellClick}
            onCellDoubleClick={this.onCellDoubleClick}
            activeCell={this.state.activeCell}
            focusedCell={this.state.focusedCell}
            // Pass disabled cells for this row only.
            disabledCells={this.props.disabledCells?.filter(({ x }: { x: number }) => {
              return x === startIndex + i;
            })}
            height={this.props.rowHeight}
            columnWidthValues={this.props.columnWidthValues}
          />
        );
      });
    } else {
      body = <SpreadsheetGridPlaceholder>{this.props.placeholder}</SpreadsheetGridPlaceholder>;
    }

    return body;
  }

  render() {
    return (
      <SpreadsheetGridStyle
        style={{
          transform: `translate3d(0, ${this.calculatePosition()}, 0)`,
        }}
      >
        {this.renderBody()}
      </SpreadsheetGridStyle>
    );
  }
}

export default SpreadsheetGrid;
